import { GatewayDevice } from '@store';
import {
  ActivateDeactivateCommands,
  DeviceRoles,
  Feature,
  IsActiveProperty,
  PVExcessDeviceType,
  PVExcessOutflowTypes,
} from '@vi-iot/client/index';
import { EnergyFlowOutType } from 'src/app/operational-data-common/translation-pipes/translate-energy-flow-type.pipe';

export namespace PVPowerPrioritization {
  export const LIST_DEVICE_ITEM_KEYS: {
    INFO_KEYS: Record<string, string>;
    SUBTITLE_KEYS: Record<string, string>;
    DEVICE_NAMES: Record<string, string>;
  } = {
    INFO_KEYS: {
      [EnergyFlowOutType.Heatpump]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_INFO',
      [PVExcessOutflowTypes.OBNG_HEATPUMP]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_INFO',
      [EnergyFlowOutType.VCS]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.CHARGING_STATION_INFO',
      [PVExcessOutflowTypes.Battery]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.BATTERY_INFO',
    },
    SUBTITLE_KEYS: {
      [EnergyFlowOutType.Heatpump]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_SUBTITLE',
      [PVExcessOutflowTypes.OBNG_HEATPUMP]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_SUBTITLE',
      [EnergyFlowOutType.DryContactHeatpump]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_SUBTITLE',
      [EnergyFlowOutType.DryContactGeneric]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_SUBTITLE',
      [EnergyFlowOutType.PhotovoltaicsHeater]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.HEATPUMP_SUBTITLE',
      [EnergyFlowOutType.VCS]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.CHARGING_STATION_SUBTITLE',
      [PVExcessOutflowTypes.Battery]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PV_POWER_PRIORITIZATION.BATTERY_SUBTITLE',
    },
    DEVICE_NAMES: {
      [EnergyFlowOutType.Heatpump]: '+HEMS.SETTINGS.OPTIMIZATIONS.PHOTOVOLTAIC_EXCESS.TITLE',
      [PVExcessOutflowTypes.OBNG_HEATPUMP]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PHOTOVOLTAIC_EXCESS.TITLE',
      [EnergyFlowOutType.DryContactHeatpump]:
        '+HEMS.SETTINGS.OPTIMIZATIONS.PHOTOVOLTAIC_EXCESS.TITLE',
      [EnergyFlowOutType.VCS]: '+INSTALLATIONS.INSTALLATIONS.WALLBOX.WALLBOX_TILE',
      [PVExcessOutflowTypes.Battery]: '+INSTALLATIONS.INSTALLATION.BATTERY.TITLE',
      [EnergyFlowOutType.PhotovoltaicsHeater]:
        '+INSTALLATIONS.INSTALLATION.PV_HEATER.PV_IMMERSION_HEATER',
      [EnergyFlowOutType.DryContactGeneric]:
        '+INSTALLATIONS.DRY_CONTACTS.GENERIC_CONSUMER.OTHER_DEVICE',
    },
  };

  export const MAP_HEMS_ID_TO_HOCA_FEATURE: Record<string, string> = {
    '2': 'photovoltaicExcessToDryContactControlledDeviceOne',
    '3': 'photovoltaicExcessToDryContactControlledDeviceTwo',
    '4': 'photovoltaicExcessToDryContactControlledDeviceThree',
    '5': 'photovoltaicExcessToDryContactControlledDeviceFour',
    '6': 'photovoltaicExcessToDryContactControlledDeviceFive',
  };

  export function mapHemsIdToHocaFeatureName(id: string): string {
    const hemsId = id.split('-')[1];

    return PVPowerPrioritization.MAP_HEMS_ID_TO_HOCA_FEATURE[hemsId] || '';
  }

  export function getInfoKey(type: PVExcessDeviceType): string {
    return PVPowerPrioritization.LIST_DEVICE_ITEM_KEYS.INFO_KEYS[type] || '';
  }

  export function getSubTitleKey(type: PVExcessDeviceType, isOptimizationActive: boolean): string {
    if (type === PVExcessOutflowTypes.Battery) {
      return isOptimizationActive
        ? PVPowerPrioritization.LIST_DEVICE_ITEM_KEYS.SUBTITLE_KEYS[type]
        : '';
    }

    return !isOptimizationActive
      ? PVPowerPrioritization.LIST_DEVICE_ITEM_KEYS.SUBTITLE_KEYS[type] || ''
      : '';
  }

  export function isWarningIconVisible(
    type: PVExcessDeviceType,
    isOptimizationActive: boolean
  ): boolean {
    if (type === PVExcessOutflowTypes.Battery) {
      return false;
    }

    return !isOptimizationActive;
  }

  export function getDeviceType(deviceDetails: GatewayDevice): PVExcessDeviceType {
    const roles = deviceDetails?.roles || [];

    if (roles.includes(DeviceRoles.Heatpump)) {
      return EnergyFlowOutType.Heatpump;
    } else if (
      roles.includes(DeviceRoles.VCS) ||
      roles.includes(DeviceRoles.InterfaceBatteryVehicleChargingStation)
    ) {
      return EnergyFlowOutType.VCS;
    } else if (roles.includes(DeviceRoles.ProductVitocharge)) {
      return PVExcessOutflowTypes.Battery;
    } else if (roles.includes(DeviceRoles.PhotovoltaicSolarLogInverter)) {
      return EnergyFlowOutType.PhotovoltaicsHeater;
    } else if (
      roles.includes(DeviceRoles.InterfaceDryContact) &&
      roles.includes(DeviceRoles.GenericConsumer)
    ) {
      return EnergyFlowOutType.DryContactGeneric;
    } else if (
      roles.includes(DeviceRoles.InterfaceDryContact) &&
      roles.includes(DeviceRoles.InterfaceCompressor)
    ) {
      return EnergyFlowOutType.DryContactHeatpump;
    } else if (roles.includes(DeviceRoles.OBNG) && roles.includes(DeviceRoles.SystemController)) {
      return PVExcessOutflowTypes.OBNG_HEATPUMP;
    } else if (roles.includes(DeviceRoles.SolarLogPVHeater)) {
      return EnergyFlowOutType.PhotovoltaicsHeater;
    }
  }

  export function getHeatpumpOptimizationActiveStatus(
    pvExcesstoHeatPump: Feature<IsActiveProperty, ActivateDeactivateCommands>,
    emsHocaHeatPumponly: Feature<IsActiveProperty, ActivateDeactivateCommands>
  ): Feature<IsActiveProperty, ActivateDeactivateCommands> {
    const isPvExcesstoHeatpumpActive = pvExcesstoHeatPump?.properties?.active?.value ?? false;
    const isEMSFeatureHeatpumpOnlyActive = emsHocaHeatPumponly?.properties?.active?.value ?? false;

    return {
      properties: {
        active: {
          value: isPvExcesstoHeatpumpActive || isEMSFeatureHeatpumpOnlyActive,
        },
      },
    } as Feature<IsActiveProperty, ActivateDeactivateCommands>;
  }
}
